import React, { useState } from "react";
import { toast } from "sonner";
import { QRCodeSVG } from "qrcode.react";
import { Send, ScanLine } from "lucide-react";
import { wa, getCampaign } from "../mock";
import { ContactLink } from "./ContactLink";
import { trackEvent, trackAdsConversion, ADS_QUOTE_LABEL } from "../lib/analytics";

const FIELDS = [
  { name: "name", label: "Name", type: "text", required: true, autoComplete: "name" },
  { name: "company", label: "Company", type: "text", autoComplete: "organization" },
  { name: "phone", label: "Phone", type: "tel", required: true, autoComplete: "tel" },
  { name: "city", label: "City", type: "text", placeholder: "Noida / Delhi / Gurgaon / Bangalore" }
];

export default function Contact() {
  const [form, setForm] = useState({ name: "", company: "", phone: "", city: "", requirement: "", website: "" });
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.phone) {
      toast.error("Please add your name and phone number.");
      return;
    }
    setBusy(true);
    const msg = `Hi Kapatia, enquiry from ${form.name}${form.company ? " (" + form.company + ")" : ""}. Phone: ${form.phone}. City: ${form.city || "-"}. Requirement: ${form.requirement || "-"}`;
    try {
      const r = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/enquiries`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ...form, source: "website", campaign: getCampaign() })
      });
      if (!r.ok) throw new Error("failed");
      trackEvent("form_submission", { form_name: "enquiry", city: form.city || undefined, page_path: window.location.pathname });
      trackEvent("generate_lead", { form_name: "enquiry", city: form.city || undefined, has_company: !!form.company, page_path: window.location.pathname });
      trackAdsConversion(ADS_QUOTE_LABEL);
      toast.success("Enquiry received! We'll respond shortly. Opening WhatsApp\u2026");
      setForm({ name: "", company: "", phone: "", city: "", requirement: "", website: "" });
    } catch (_) {
      toast.warning("Saved locally \u2014 opening WhatsApp so your enquiry reaches us.");
    } finally {
      setBusy(false);
      // Automatic WhatsApp handoff after submit is NOT a deliberate click -> no whatsapp_click event.
      setTimeout(() => {
        window.open(wa(msg), "_blank", "noopener");
      }, 600);
    }
  };

  return (
    <section id="contact" data-testid="contact-section" className="home-section" style={{ scrollMarginTop: 120, background: "var(--paper)" }}>
      <div className="container grid items-start gap-10 lg:grid-cols-[0.85fr_1fr]">
        <div className="reveal">
          <p className="eyebrow">Contact Kapatia</p>
          <h2 data-testid="contact-heading" className="mt-3 font-heading leading-[1.1]"
              style={{ color: "var(--green-950)", fontSize: "clamp(24px,3vw,36px)" }}>
            Get best price and quick response
          </h2>
          <p className="mt-3 text-[17px]" style={{ color: "var(--muted)" }}>
            Send your requirement for selling assets, buying pre-owned equipment, corporate buyback
            or new branded IT products.
          </p>
          <div className="mt-5 grid gap-2">
            <ContactLink testId="contact-phone" variant="card" label="Phone / WhatsApp"
              className="rounded-xl border bg-white p-3"
              style={{ borderColor: "var(--line)", borderLeft: "6px solid var(--orange-500)", boxShadow: "0 10px 24px rgba(45,106,79,0.06)" }} />
            <ContactLink kind="email" testId="contact-email" variant="card" label="Email"
              className="rounded-xl border bg-white p-3"
              style={{ borderColor: "var(--line)", borderLeft: "6px solid var(--orange-500)", boxShadow: "0 10px 24px rgba(45,106,79,0.06)" }} />
            <div className="flex items-center gap-4 rounded-xl border bg-white p-3"
                 style={{ borderColor: "var(--line)", borderLeft: "6px solid var(--orange-500)", boxShadow: "0 10px 24px rgba(45,106,79,0.06)" }}>
              <div className="rounded-lg bg-white p-2" style={{ border: "1px solid var(--line)" }}>
                <QRCodeSVG value={wa()}
                           size={92} bgColor="#ffffff" fgColor="var(--green-800)" level="M" />
              </div>
              <span>
                <span className="flex items-center gap-1.5 text-[12px] font-bold uppercase tracking-wide" style={{ color: "var(--muted)" }}>
                  <ScanLine size={14} /> Scan to chat
                </span>
                <strong className="mt-0.5 block font-heading text-[18px] font-extrabold" style={{ color: "var(--green-950)" }}>Instant WhatsApp chat</strong>
                <span className="block text-[13px]" style={{ color: "var(--muted)" }}>Point your phone camera at the code</span>
              </span>
            </div>
          </div>
        </div>

        <form id="enquiry-form" data-testid="contact-enquiry-form" onSubmit={submit} className="reveal grid gap-4 rounded-2xl border bg-white p-6 md:p-7"
              style={{ borderColor: "var(--line)", boxShadow: "var(--shadow)", scrollMarginTop: 120 }}>
          <h3 data-testid="contact-form-heading" className="font-heading text-[22px] font-semibold" style={{ color: "var(--green-950)" }}>Quick enquiry</h3>
          <input type="text" tabIndex={-1} autoComplete="off" aria-hidden="true" data-testid="enquiry-website"
                 value={form.website} onChange={set("website")} name="website"
                 style={{ position: "absolute", left: "-9999px", width: 1, height: 1, opacity: 0 }} />
          {FIELDS.map((f) => (
            <label key={f.name} className="grid gap-1.5 font-heading text-[14px] font-semibold" style={{ color: "var(--green-950)" }}>
              <span>{f.label}{f.required && <span style={{ color: "var(--orange-600)" }}> *</span>}</span>
              <input type={f.type} value={form[f.name]} onChange={set(f.name)} required={f.required}
                     autoComplete={f.autoComplete} placeholder={f.placeholder} data-testid={`enquiry-${f.name}`}
                     className="w-full rounded-lg border bg-white px-3.5 py-3 font-sans text-[15px] font-normal outline-none transition-shadow focus:ring-4"
                     style={{ borderColor: "#d8c8af", color: "var(--text)" }} />
            </label>
          ))}
          <label className="grid gap-1.5 font-heading text-[14px] font-semibold" style={{ color: "var(--green-950)" }}>
            Requirement
            <textarea rows={4} value={form.requirement} onChange={set("requirement")} data-testid="enquiry-requirement"
                      placeholder="Example: 25 used laptops, 10 desktops, 2 UPS systems, office clearance, or new HP/Dell quote"
                      className="w-full rounded-lg border bg-white px-3.5 py-3 font-sans text-[15px] font-normal outline-none transition-shadow focus:ring-4"
                      style={{ borderColor: "#d8c8af", color: "var(--text)" }} />
          </label>
          <button type="submit" disabled={busy} data-testid="enquiry-submit"
                  className="kp-button inline-flex items-center justify-center gap-2 rounded-full px-6 py-3.5 font-heading font-bold text-white transition-transform hover:-translate-y-0.5 disabled:opacity-60"
                  style={{ background: "linear-gradient(135deg, #ff3f1f 0%, var(--orange-500) 58%, #ff9f1c 100%)", boxShadow: "0 16px 30px rgba(230,81,22,0.26)" }}>
            <Send size={18} /> {busy ? "Sending\u2026" : "Submit Enquiry"}
          </button>
          <a href={wa()} target="_blank" rel="noopener noreferrer" data-testid="enquiry-whatsapp"
             className="kp-button inline-flex items-center justify-center gap-2.5 rounded-full border-2 bg-white px-6 py-3 font-heading font-bold transition-transform hover:-translate-y-0.5"
             style={{ borderColor: "var(--green-800)", color: "var(--green-900)" }}>
            <img src="/assets/icons/whatsapp-clean-square.png" alt="" className="h-6 w-6" /> Send on WhatsApp
          </a>
        </form>
      </div>
    </section>
  );
}
